import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui';
import { FieldRenderer } from '../fields/FieldRenderer';
import { getVisibleGroups, isEmptyValue } from '../helpers';
import { useExistingSubmissionPrefill } from '../hooks/useExistingSubmissionPrefill';
import { validateField } from '../validators';
import type {
  ExistingSubmissionFetcher,
  FormField,
  FormSchema,
  FormStateValues,
  FormSubmitValues,
} from '../types';
import type { FormUploadFn } from '../FormUploadContext';
import { DocumentPendingReviewError } from '../../services/documentApi';
import {
  bakeFilledPdf,
  bytesToPdfFile,
  decodeBase64Pdf,
  isSignatureField,
  type SignatureInput,
} from './pdfBuilder';
import { SignaturePadField, type SignaturePadHandle } from './SignaturePadField';
import { usePdfPreview } from './usePdfPreview';

/**
 * Spec: docs/onboarding/API/PDF-Resource-Type.md
 *
 * 3-step flow this component implements:
 *   1. Decode `schema.document.document_base64` → template bytes.
 *   2. Candidate fills the form fields declared in `schema.columns` (text
 *      inputs + a signature pad). Live preview re-renders on changes.
 *   3. On submit: bake values+signatures into the PDF AcroForm (field name
 *      == form item title), flatten, upload via `uploadFile` (which the
 *      modal binds to the document API), and POST the workflow event body
 *      `{document_version_id}` via `onSubmit`.
 */

export interface PdfSurveySubmitPayload extends FormSubmitValues {
  document_version_id: number;
}

interface Props {
  schema: FormSchema;
  /**
   * Required to upload the baked PDF. The modal wires this to
   * `inviteService.uploadDocument` (candidate) or `onboardingService.uploadDocument`
   * (backoffice) bound to the active `case_id`.
   */
  uploadFile: FormUploadFn;
  /**
   * Submits the workflow event body. The modal wires this to `submitEvent`.
   * Return type is intentionally `unknown` so callers can pass through a
   * mutation that resolves with the server response.
   */
  onSubmit: (payload: PdfSurveySubmitPayload) => void | Promise<unknown>;
  onCancel?: () => void;
  submitting?: boolean;
  submitLabel?: string;
  /** Banner shown above the submit button, typically a server error. */
  serverError?: string | null;
  /**
   * Lookup-derived body keys (e.g. `filled_by` from a `user.email` lookup).
   * `lookup_key: "ip"` is server-derived and must NOT be sent — the modal
   * already filters those out.
   */
  lookupAdditions?: FormSubmitValues;
  /**
   * Optional existing-submission prefill — same contract as `DynamicForm`.
   * Used to populate visible fields (e.g. `location`) when the candidate
   * has previously saved progress for this `(entity_id, form)` pair.
   * Signature fields can't be visually restored, but the form value is
   * still set so the picker isn't blank-key on submit.
   */
  existingSubmissionFetcher?: ExistingSubmissionFetcher;
  externalIdentifiers?: Record<string, string | number | undefined | null>;
}

const FILENAME_PREFIX = 'signed_';

export const PdfSurveyForm = ({
  schema,
  uploadFile,
  onSubmit,
  onCancel,
  submitting = false,
  submitLabel = 'Sign & Submit',
  serverError = null,
  lookupAdditions = {},
  existingSubmissionFetcher,
  externalIdentifiers,
}: Props) => {
  // ── Resolve schema → fields ────────────────────────────────────────────
  const visibleGroups = useMemo(() => getVisibleGroups(schema.columns), [schema.columns]);
  const visibleFields: FormField[] = useMemo(
    () => visibleGroups.flatMap((g) => g.items),
    [visibleGroups],
  );
  const signatureFields = useMemo(
    () => visibleFields.filter((f) => isSignatureField(f)),
    [visibleFields],
  );
  const textFields = useMemo(
    () => visibleFields.filter((f) => !isSignatureField(f)),
    [visibleFields],
  );

  // ── Template ───────────────────────────────────────────────────────────
  const document = schema.document;
  const templateBytes = useMemo<Uint8Array | null>(() => {
    if (!document?.document_base64) return null;
    try {
      return decodeBase64Pdf(document.document_base64);
    } catch (err) {
      // Decoding errors are non-recoverable for this view — surface below.
      console.error('PdfSurveyForm: failed to decode document_base64', err);
      return null;
    }
  }, [document?.document_base64]);

  // ── Form state ─────────────────────────────────────────────────────────
  const [values, setValues] = useState<FormStateValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Submit-time error. We own this internally because the upload step (which
  // runs before `onSubmit`) can fail with its own errors that the parent's
  // mutation never sees.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pendingSubmissionId, setPendingSubmissionId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const signaturePadsRef = useRef<Record<string, SignaturePadHandle | null>>({});
  // Monotonic revision counter bumped on every signature change (stroke end
  // or clear). A boolean "has content" flag wasn't enough — once the first
  // stroke flipped it to `true`, subsequent strokes returned the same value
  // and the preview useEffect dep never changed, so multi-stroke signatures
  // showed only the first stroke in the live preview.
  const [signatureRevision, setSignatureRevision] = useState(0);

  // Merging setter for the existing-submission prefill hook — the hook
  // expects "apply these keys" semantics, not "replace whole state".
  const mergeValues = useCallback((next: FormStateValues) => {
    setValues((prev) => ({ ...prev, ...next }));
  }, []);

  useExistingSubmissionPrefill({
    schema,
    values,
    setValues: mergeValues,
    fetcher: existingSubmissionFetcher,
    externalIdentifiers,
  });

  const setValue = useCallback((title: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [title]: value }));
    setErrors((prev) => {
      if (!(title in prev)) return prev;
      const next = { ...prev };
      delete next[title];
      return next;
    });
  }, []);

  // ── Live preview ───────────────────────────────────────────────────────
  // The preview re-bakes the PDF whenever inputs change (debounced). Heavy
  // but matches the documented POC's behavior and gives the candidate
  // immediate feedback before they commit.
  const [previewBytes, setPreviewBytes] = useState<Uint8Array | null>(null);
  const previewTokenRef = useRef(0);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!templateBytes) {
      setPreviewBytes(null);
      return;
    }
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(async () => {
      const token = ++previewTokenRef.current;
      try {
        const textValues = collectTextValues(textFields, values);
        const signatures = collectSignatures(signatureFields, signaturePadsRef.current);
        const baked = await bakeFilledPdf({
          templateBytes,
          textValues,
          signatures,
          flatten: false,
        });
        if (token !== previewTokenRef.current) return;
        setPreviewBytes(baked);
      } catch (err) {
        // Preview failure doesn't block the form; keep last bytes.
        console.warn('PdfSurveyForm: preview bake failed', err);
      }
    }, 300);
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [templateBytes, textFields, signatureFields, values, signatureRevision]);

  const preview = usePdfPreview({ bytes: previewBytes });

  // ── Submit ─────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const next: Record<string, string> = {};
    for (const f of textFields) {
      const err = validateField(f, values[f.title]);
      if (err) next[f.title] = err;
    }
    for (const f of signatureFields) {
      if (!f.rqd) continue;
      const pad = signaturePadsRef.current[f.title];
      if (!pad || pad.isEmpty()) {
        next[f.title] = `${f.display_title || f.title} is required`;
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting || uploading) return;
    if (!templateBytes) return;
    if (!document) return;
    if (!validate()) return;

    setSubmitError(null);
    setPendingSubmissionId(null);
    setUploading(true);
    try {
      const textValues = collectTextValues(textFields, values);
      const signatures = collectSignatures(signatureFields, signaturePadsRef.current);
      const baked = await bakeFilledPdf({
        templateBytes,
        textValues,
        signatures,
        flatten: true,
      });
      const filename = `${FILENAME_PREFIX}${schema.name || 'document'}.pdf`;
      const file = bytesToPdfFile(baked, filename);

      const uploaded = await uploadFile(file, { documentTypeId: document.document_type_id });

      // Workflow POST body for pdf_survey: include every visible form field
      // so the workflow records them alongside the uploaded document.
      //   - text fields → user's typed value (empties are dropped by
      //     `collectTextValues`, matching regular form behavior).
      //   - signature fields → empty string. The actual signature image is
      //     baked into the PDF that was just uploaded; the workflow only
      //     needs a placeholder at the key to satisfy server-side form
      //     validation.
      const formValues: Record<string, unknown> = { ...textValues };
      for (const f of signatureFields) {
        formValues[f.title] = '';
      }

      await onSubmit({
        ...formValues,
        document_version_id: uploaded.document_version_id,
        ...lookupAdditions,
      });
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        // User-initiated cancel; no error UI needed.
        return;
      }
      if (err instanceof DocumentPendingReviewError) {
        setPendingSubmissionId(err.pendingSubmissionId ?? null);
        setSubmitError(err.message);
        return;
      }
      setSubmitError(err?.message || 'Could not submit. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (!document) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        This step is misconfigured — the server did not return a document. Please contact support.
      </div>
    );
  }
  if (!templateBytes) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Could not decode the document. Please refresh and try again.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Inputs panel */}
      <section className="card">
        <div className="card-body space-y-4">
          {textFields.map((field) => (
            <FieldRenderer
              key={field.title}
              field={field}
              value={values[field.title]}
              error={errors[field.title]}
              onChange={(next) => setValue(field.title, next)}
            />
          ))}
          {signatureFields.map((field) => (
            <SignaturePadField
              key={field.title}
              label={field.display_title || field.title}
              required={!!field.rqd}
              error={errors[field.title]}
              ref={(handle) => {
                signaturePadsRef.current[field.title] = handle;
              }}
              onChange={() => setSignatureRevision((v) => v + 1)}
            />
          ))}

          {pendingSubmissionId !== null ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-1">
              <div className="font-semibold">Submission pending review</div>
              <p>{submitError}</p>
              <p className="text-xs">
                Existing submission reference:{' '}
                <span className="font-mono">#{pendingSubmissionId}</span>
              </p>
            </div>
          ) : submitError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {submitError}
            </div>
          ) : serverError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {serverError}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            {onCancel && (
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={submitting || uploading}
              >
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={submitting || uploading || pendingSubmissionId !== null}>
              {uploading ? 'Uploading…' : submitting ? 'Submitting…' : submitLabel}
            </Button>
          </div>
        </div>
      </section>

      {/* Preview panel */}
      <section className="card">
        <div className="card-body">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-rgf-gray-900">
              Live preview
              {preview.pageCount > 0 && (
                <span className="ml-2 text-xs font-normal text-rgf-gray-500">
                  ({preview.pageCount} page{preview.pageCount === 1 ? '' : 's'})
                </span>
              )}
            </h3>
            <span className="text-xs text-rgf-gray-500">
              {preview.status === 'loading' && 'Updating…'}
              {preview.status === 'ready' && 'Up to date'}
              {preview.status === 'error' && (preview.error || 'Preview error')}
              {preview.status === 'idle' && 'Loading…'}
            </span>
          </div>
          <div
            ref={preview.containerRef}
            className="border border-rgf-gray-200 rounded-md bg-rgf-gray-50 p-2 max-h-[80vh] overflow-y-auto"
          />
        </div>
      </section>
    </form>
  );
};

// ── helpers ──────────────────────────────────────────────────────────────

const collectTextValues = (
  fields: FormField[],
  values: FormStateValues,
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const f of fields) {
    const v = values[f.title];
    if (isEmptyValue(v)) continue;
    out[f.title] = typeof v === 'string' ? v : String(v);
  }
  return out;
};

const collectSignatures = (
  fields: FormField[],
  pads: Record<string, SignaturePadHandle | null>,
): SignatureInput[] => {
  const out: SignatureInput[] = [];
  for (const f of fields) {
    const pad = pads[f.title];
    if (!pad || pad.isEmpty()) continue;
    const dataUrl = pad.toDataUrl();
    if (!dataUrl) continue;
    out.push({ fieldName: f.title, data: dataUrl });
  }
  return out;
};
